<template>
    <Dialog :open="open" @update:open="handleClose">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Invite team member</DialogTitle>
                <DialogDescription>
                    Send an invitation to a new team member.
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4">
                <div class="space-y-2">
                    <Label for="invite-email">Email address</Label>
                    <Input
                        id="invite-email"
                        v-model="form.email"
                        type="email"
                        placeholder="Enter email address"
                        :class="{ 'border-destructive': emailError }"
                        @blur="touchEmail"
                    />
                    <p v-if="emailError" class="text-xs text-destructive">
                        {{ emailError }}
                    </p>
                </div>

                <div class="space-y-2">
                    <Label for="invite-role">Team role</Label>
                    <Select v-model="form.role">
                        <SelectTrigger id="invite-role">
                            <SelectValue placeholder="Choose a role" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem
                                v-for="role in roles"
                                :key="role.value"
                                :value="role.value"
                            >
                                {{ role.label }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <p v-if="roleError" class="text-xs text-destructive">
                        {{ roleError }}
                    </p>
                </div>

                <div
                    v-if="form.role && form.role !== 'viewer' && !isPersonalTeam"
                    class="flex items-start gap-3 rounded-md bg-new-amber-50 border border-new-amber-200 p-3"
                >
                    <TriangleAlert class="size-4 shrink-0 text-new-amber-600 mt-0.5" />
                    <p class="text-xs text-new-amber-700">
                        Team members with Administrator or Editor roles are
                        billed additionally according to your TOPOL Pro plan.
                        Billing starts only when the new team member accepts your invitation.
                    </p>
                </div>
            </div>

            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="handleClose">
                    Cancel
                </Button>
                <Button
size="lg"
                    :disabled="loading || !isValid"
                    :loading="loading"
                    @click="sendInvite"
                >
                    Invite member
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { route } from 'ziggy-js'
import { toast } from 'vue-sonner'
import { TriangleAlert } from 'lucide-vue-next'
import { useGtm } from '@/composables/useGtm'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'

interface RoleOption {
    label: string
    value: string
}

const props = defineProps<{
    open: boolean
    roles: RoleOption[]
    isPersonalTeam: boolean
}>()

const emit = defineEmits<{
    close: []
}>()

const { pushEvent } = useGtm()

const form = ref({
    email: '',
    role: '',
})
const loading = ref(false)
const emailTouched = ref(false)
const serverErrors = ref<Record<string, string>>({})

const emailError = computed(() => {
    if (serverErrors.value.email) return serverErrors.value.email
    if (!emailTouched.value) return ''
    if (!form.value.email) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) return 'Please enter a valid email'
    return ''
})

const roleError = computed(() => {
    if (serverErrors.value.role) return serverErrors.value.role
    return ''
})

const isValid = computed(() => {
    return (
        form.value.email &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email) &&
        form.value.role
    )
})

const touchEmail = () => {
    emailTouched.value = true
}

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen && props.roles.length === 1) {
            form.value.role = props.roles[0].value
        }
    },
    { immediate: true }
)

const handleClose = () => {
    form.value = { email: '', role: '' }
    emailTouched.value = false
    serverErrors.value = {}
    emit('close')
}

const sendInvite = () => {
    emailTouched.value = true
    if (!isValid.value) return

    loading.value = true
    router.post(route('members.store'), form.value, {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Invitation sent')
            pushEvent('topolpro_add_member')
            handleClose()
        },
        onError: (err) => {
            const errorMessage = err.error || 'There was an error sending your invitation.'
            toast.error(errorMessage)
            serverErrors.value = err
        },
        onFinish: () => {
            loading.value = false
        },
    })
}
</script>
